import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { dateStrInAppTz } from "../../../utils/appTime";
import { withAuth } from "../../../components/HOC/withAuth";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import Checkbox from "../../../components/BaseUI/Checkbox";
import Select from "../../../components/BaseUI/Select";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";

import {
  FiShoppingCart,
  FiCalendar,
  FiDollarSign,
  FiCreditCard,
  FiServer,
  FiTrash2,
  FiClock,
  FiPackage,
  FiCheckCircle,
  FiFileText,
  FiRefreshCw,
  FiPercent,
} from "react-icons/fi";
import {
  clampPct,
  calculateListSubtotal,
  calculateFinalTotal,
  getLineDiscountInfo,
} from "../../../utils/saleDiscountCalc";
// Shared price display: follows the system USD/TL toggle in the header
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import { RiFileTransferLine } from "react-icons/ri";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const orderHeaderKindSelectOptions = [
  { value: "percent", label: "Yuzde (%)" },
  { value: "fixed", label: "Sabit tutar (USD)" },
];

const CreateDeviceSale = () => {
  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [bulkDiscountModelIds, setBulkDiscountModelIds] = useState([]);
  const [bulkDiscountPercent, setBulkDiscountPercent] = useState("");

  const [saleData, setSaleData] = useState({
    saleName: "",
    companyId: null,
    sequence: "",
    saleDate: dateStrInAppTz(new Date()),
    saleTime: new Date().toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    }),
    totalDevices: 0,
    paymentMethod: "cash",
    paymentStatus: "pending",
    notes: "",
    discountScope: "order_total",
    orderHeaderKind: "percent",
    orderDiscountPercent: 0,
    orderDiscountFixed: 0,
    exchangeRate: "",
  });
  const [isSaleNameManual, setIsSaleNameManual] = useState(false);

  const fx = useExchangeRateOptional();

  const parseExchangeRate = (val) => {
    if (val === undefined || val === null || val === "") return undefined;
    const num = Number(String(val).trim().replace(",", "."));
    return !isNaN(num) && num > 0 ? num : undefined;
  };

  useEffect(() => {
    if (fx?.rate && !saleData.exchangeRate) {
      setSaleData((prev) => ({ ...prev, exchangeRate: String(fx.rate) }));
    }
  }, [fx?.rate]);

  const format = useFormatValue;
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/device-sales",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.DEVICE_SALES"), url: "/device-sales" },
    { label: translate("UI.ADD") },
  ];

  useEffect(() => {
    fetchModels();
    fetchCompanies();
  }, []);

  useEffect(() => {
    updateTotalDevices();
  }, [selectedModels]);

  const getMonthYearFromDate = (dateStr) => {
    const d = dateStr ? new Date(dateStr) : new Date();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear() % 100).padStart(2, "0");
    return `${month}${year}`; // MMYY
  };

  const buildSaleName = (companyCode, dateStr, sequence) => {
    if (!companyCode || !sequence) return "";
    const monthYear = getMonthYearFromDate(dateStr);
    return `${companyCode}/${monthYear}/${sequence}`;
  };

  const fetchSoldSequenceForCompany = useCallback(
    async (companyId, dateStr) => {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/device-sales`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const salesList = response.data?.sales;
      const monthYear = getMonthYearFromDate(dateStr);
      const count = Array.isArray(salesList)
        ? salesList.filter((s) => {
            const sd = s?.saleDate ? new Date(s.saleDate) : null;
            if (!sd || Number.isNaN(sd.getTime())) return false;
            const m = String(sd.getMonth() + 1).padStart(2, "0");
            const y = String(sd.getFullYear() % 100).padStart(2, "0");
            return `${m}${y}` === monthYear;
          }).length
        : 0;
      return count + 1;
    },
    [getToken],
  );

  // Generate sequence automatically based on existing sales for (company + month/year)
  useEffect(() => {
    if (!saleData.companyId) {
      setSaleData((prev) => ({ ...prev, sequence: "", saleName: "" }));
      setIsSaleNameManual(false);
      return;
    }

    // Clear previous auto-generated values until we fetch the next sequence
    setSaleData((prev) => ({ ...prev, sequence: "", saleName: "" }));
    setIsSaleNameManual(false);

    let cancelled = false;
    (async () => {
      try {
        const seq = await fetchSoldSequenceForCompany(
          saleData.companyId,
          saleData.saleDate,
        );
        if (cancelled) return;
        setSaleData((prev) => ({
          ...prev,
          sequence: seq?.toString?.() || "",
        }));
      } catch (e) {
        console.error("Failed to generate sale sequence:", e);
        if (cancelled) return;
        setSaleData((prev) => ({ ...prev, sequence: "", saleName: "" }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [saleData.companyId, saleData.saleDate, fetchSoldSequenceForCompany]);

  // Auto-generate sale name: "companyCode/MMYY/sequence"
  useEffect(() => {
    const company = companies.find((c) => c._id === saleData.companyId);
    if (!company?.code || !saleData.sequence) return;

    const nextSaleName = buildSaleName(
      company.code,
      saleData.saleDate,
      saleData.sequence,
    );
    if (!nextSaleName) return;
    setSaleData((prev) => ({
      ...prev,
      saleName: isSaleNameManual ? prev.saleName : nextSaleName,
    }));
  }, [
    saleData.companyId,
    saleData.saleDate,
    saleData.sequence,
    companies,
    isSaleNameManual,
  ]);

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/device-models`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setModels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch models:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const handleAddModel = (model) => {
    const existingIndex = selectedModels.findIndex(
      (m) => m.modelId === model._id,
    );
    if (existingIndex >= 0) {
      const updated = [...selectedModels];
      updated[existingIndex].count += 1;
      setSelectedModels(updated);
    } else {
      setSelectedModels([
        ...selectedModels,
        {
          modelId: model._id,
          code: model.code,
          name: model.name,
          count: 1,
          price: model.price || 0,
          discountPercent: 0,
        },
      ]);
    }
    setShowModelPicker(false);
    updateTotalDevices();
  };

  const handleRemoveModel = (modelId) => {
    setSelectedModels(selectedModels.filter((m) => m.modelId !== modelId));
    updateTotalDevices();
  };

  const handleUpdateModelCount = (modelId, count) => {
    if (count <= 0) {
      handleRemoveModel(modelId);
      return;
    }
    const updated = selectedModels.map((m) =>
      m.modelId === modelId ? { ...m, count } : m,
    );
    setSelectedModels(updated);
    updateTotalDevices();
  };

  const updateTotalDevices = () => {
    const total = selectedModels.reduce((sum, m) => sum + m.count, 0);
    setSaleData((prev) => ({ ...prev, totalDevices: total }));
  };

  const discountOpts = useMemo(() => {
    const list = calculateListSubtotal(selectedModels);
    return {
      discountScope: saleData.discountScope,
      orderHeaderKind: saleData.orderHeaderKind,
      orderDiscountPercent: saleData.orderDiscountPercent,
      orderDiscountFixed: saleData.orderDiscountFixed,
      listSubtotal: list,
    };
  }, [
    selectedModels,
    saleData.discountScope,
    saleData.orderHeaderKind,
    saleData.orderDiscountPercent,
    saleData.orderDiscountFixed,
  ]);

  const lineNetTotal = (m) => getLineDiscountInfo(m, discountOpts).net;

  const calculateLineNetSum = () =>
    selectedModels.reduce((sum, m) => sum + lineNetTotal(m), 0);

  const calculateTotalAmount = () =>
    calculateFinalTotal(selectedModels, discountOpts);

  const hasActiveDiscount = useMemo(() => {
    if (saleData.discountScope === "order_total") {
      if (saleData.orderHeaderKind === "percent") {
        return clampPct(saleData.orderDiscountPercent) > 0;
      }
      return (Number(saleData.orderDiscountFixed) || 0) > 0;
    }
    return selectedModels.some((m) => clampPct(m.discountPercent) > 0);
  }, [
    saleData.discountScope,
    saleData.orderHeaderKind,
    saleData.orderDiscountPercent,
    saleData.orderDiscountFixed,
    selectedModels,
  ]);

  const clearAllDiscounts = () => {
    setSaleData((p) => ({
      ...p,
      discountScope: "order_total",
      orderDiscountPercent: 0,
      orderDiscountFixed: 0,
      orderHeaderKind: "percent",
    }));
    setSelectedModels((prev) =>
      prev.map((m) => ({ ...m, discountPercent: 0 })),
    );
    setBulkDiscountModelIds([]);
    setBulkDiscountPercent("");
  };

  const setDiscountScope = (scope) => {
    if (scope === "order_total") {
      setSaleData((p) => ({ ...p, discountScope: "order_total" }));
      setSelectedModels((prev) =>
        prev.map((m) => ({ ...m, discountPercent: 0 })),
      );
      setBulkDiscountModelIds([]);
      setBulkDiscountPercent("");
    } else {
      setSaleData((p) => ({
        ...p,
        discountScope: "per_line",
        orderDiscountPercent: 0,
        orderDiscountFixed: 0,
        orderHeaderKind: "percent",
      }));
    }
  };

  const handleLineDiscountChange = (modelId, raw) => {
    const n = Math.min(
      100,
      Math.max(0, parseFloat(String(raw).replace(",", ".")) || 0),
    );
    setSelectedModels((prev) =>
      prev.map((m) =>
        m.modelId === modelId ? { ...m, discountPercent: n } : m,
      ),
    );
  };

  const toggleBulkDiscountModel = (modelId, checked) => {
    setBulkDiscountModelIds((prev) => {
      if (checked) {
        return prev.includes(modelId) ? prev : [...prev, modelId];
      }
      return prev.filter((id) => id !== modelId);
    });
  };

  const toggleSelectAllBulkDiscountModels = (checked) => {
    if (checked) {
      setBulkDiscountModelIds(selectedModels.map((m) => m.modelId));
    } else {
      setBulkDiscountModelIds([]);
    }
  };

  const applyBulkLineDiscount = () => {
    if (bulkDiscountModelIds.length === 0) {
      setToasts([
        {
          id: Date.now(),
          title: "Hata!",
          description: "Once en az bir model secin.",
          appearance: "danger",
        },
      ]);
      return;
    }
    const n = Math.min(
      100,
      Math.max(
        0,
        parseFloat(String(bulkDiscountPercent).replace(",", ".")) || 0,
      ),
    );
    const idSet = new Set(bulkDiscountModelIds);
    setSelectedModels((prev) =>
      prev.map((m) =>
        idSet.has(m.modelId) ? { ...m, discountPercent: n } : m,
      ),
    );
    setBulkDiscountPercent("");
  };

  const openDiscountModal = () => {
    setBulkDiscountModelIds([]);
    setBulkDiscountPercent("");
    setShowDiscountModal(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!saleData.companyId) {
        setToasts([
          {
            description:
              translate("UI.SELECT_COMPANY_REQUIRED") ||
              "Lutfen bir cari secin.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      let sequenceToUse = saleData.sequence;
      if (!sequenceToUse) {
        try {
          sequenceToUse = (
            await fetchSoldSequenceForCompany(
              saleData.companyId,
              saleData.saleDate,
            )
          )?.toString?.();
          setSaleData((prev) => ({ ...prev, sequence: sequenceToUse }));
        } catch (e) {
          setToasts([
            {
              description:
                e.response?.data?.message || "Sira numarasi uretilemedi.",
              id: Date.now(),
              title: "Hata!",
              appearance: "danger",
            },
          ]);
          return;
        }
      }

      const company = companies.find((c) => c._id === saleData.companyId);
      if (!company?.code) {
        setToasts([
          {
            description: "Firma kodu bulunamadi. Lutfen sayfayi yenileyin.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      const expectedSaleName = buildSaleName(
        company.code,
        saleData.saleDate,
        sequenceToUse,
      );

      const saleNameToUse = saleData.saleName?.trim()
        ? saleData.saleName
        : expectedSaleName;

      if (!saleNameToUse || saleNameToUse.trim() === "") {
        setToasts([
          {
            description:
              translate("UI.SALE_NAME_REQUIRED") || "Lutfen satis adini girin.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      if (selectedModels.length === 0) {
        setToasts([
          {
            description:
              translate("UI.PLEASE_SELECT_MODELS") ||
              translate("UI.PLEASE_SELECT_DEVICES") ||
              "Lutfen en az bir model secin.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      if (!saleData.paymentMethod || saleData.paymentMethod.trim() === "") {
        setToasts([
          {
            description:
              translate("UI.PAYMENT_METHOD_REQUIRED") ||
              "Lutfen odeme yontemi secin.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      const salePayload = {
        saleName: saleNameToUse,
        saleDate: saleData.saleDate,
        saleTime: saleData.saleTime,
        companyId: saleData.companyId || undefined,
        totalDevices: saleData.totalDevices,
        totalAmount: calculateTotalAmount(),
        exchangeRate:
          parseExchangeRate(saleData.exchangeRate) ??
          parseExchangeRate(fx?.rate) ??
          undefined,
        models: selectedModels.map((m) => ({
          modelId: m.modelId,
          count: m.count,
        })),
        paymentMethod: saleData.paymentMethod,
        paymentStatus: saleData.paymentStatus,
        notes: saleData.notes || "",
      };

      const response = await axios.post(
        `${API_URL}/api/device-sales`,
        salePayload,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: response.data.message || "Stok başarıyla oluşturuldu.",
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);

        setTimeout(() => {
          navigate("/device-sales");
        }, 1500);
      }
    } catch (error) {
      console.error("Stok oluşturma hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Stok kaydedilirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const handleCompanySelect = (company) => {
    if (!company || !company._id) {
      console.error("Company or company._id is undefined");
      return;
    }
    setSaleData((prevState) => ({
      ...prevState,
      companyId: company._id,
    }));
  };

  const handleDateChange = async (value) => {
    setSaleData((prevState) => ({
      ...prevState,
      saleDate: value,
    }));
    if (value) {
      try {
        const rateRes = await axios.get(
          `${API_URL}/api/exchange-rate/by-date?date=${value}`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        if (rateRes.data?.rate) {
          setSaleData((prev) => ({
            ...prev,
            exchangeRate: String(rateRes.data.rate),
          }));
        }
      } catch (err) {
        console.warn("Could not fetch rate for date:", err);
      }
    }
  };
  const handleSaleTimeChange = (value) => {
    setSaleData((prevState) => ({
      ...prevState,
      saleTime: value,
    }));
  };

  const handlePaymentMethodChange = (method) => {
    setSaleData((prevState) => ({
      ...prevState,
      paymentMethod: method,
    }));
  };

  const handlePaymentStatusChange = (status) => {
    setSaleData((prevState) => ({
      ...prevState,
      paymentStatus: status,
    }));
  };

  const handleNotesChange = (value) => {
    setSaleData((prevState) => ({
      ...prevState,
      notes: value,
    }));
  };

  const getPaymentStatusAppearance = (status) => {
    switch (status) {
      case "paid":
        return "success-subtle";
      case "pending":
        return "warning-subtle";
      case "partial":
        return "info-subtle";
      case "refunded":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  if (!canWrite) {
    return (
      <AppPage
        title={translate("UI.ADD_DEVICE_SALE")}
        breadcrumbs={breadcrumbs}
      >
        <Container>
          <ContainerItem>
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>Bu sayfada degisiklik yapma yetkiniz yok.</span>
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/device-sales")}
              >
                Geri Don
              </Button>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage title={translate("UI.ADD_DEVICE_SALE")} breadcrumbs={breadcrumbs}>
      <style>
        {`
          .app-page-header {
            margin-bottom: 1.5rem;
          }
        `}
      </style>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm modern-card">
            <div className="card-body p-4">
              <>
                {/* Stok Bilgileri */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiShoppingCart className="text-primary" />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">
                        {translate("UI.SALE_INFORMATION")}
                      </h5>
                      <p className="text-muted small mb-0">
                        {translate("UI.SALE_INFO_DESC")}
                      </p>
                    </div>
                  </div>

                  <div className="section-content">
                    <div className="d-flex justify-content-around flex-wrap gap-1">
                      <div className="col-md-4 w-45">
                        <Label>
                          {translate("UI.SELECT_COMPANY")}{" "}
                          <span className="text-danger ms-1">*</span>
                        </Label>
                        <div className="form-control-container">
                          <Dropdown
                            required
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <span className="dropdown-text">
                                  {
                                    companies.find(
                                      (company) =>
                                        company._id === saleData.companyId,
                                    )?.name
                                  }
                                </span>
                              </div>
                            }
                          >
                            <DropdownContent>
                              {/* <DropdownItem
                              onClick={() =>
                                setSaleData((prev) => ({
                                  ...prev,
                                  companyId: null,
                                }))
                              }
                            >
                              <i>{translate("UI.NO_COMPANY")}</i>
                            </DropdownItem> */}
                              {companies && companies.length > 0 ? (
                                companies.map((company) =>
                                  company && company._id ? (
                                    <DropdownItem
                                      key={company._id}
                                      onClick={() =>
                                        handleCompanySelect(company)
                                      }
                                      active={
                                        company._id === saleData.companyId
                                      }
                                    >
                                      {company.name} ({company.code})
                                    </DropdownItem>
                                  ) : null,
                                )
                              ) : (
                                <DropdownItem disabled>
                                  {translate("UI.NO_COMPANIES_AVAILABLE")}
                                </DropdownItem>
                              )}
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>

                      <div className="col-md-4 w-45 pb-5">
                        <Label>
                          {translate("UI.SALE_NAME") || "Sale Name"}{" "}
                          <span className="text-danger ms-1">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={saleData.saleName}
                          onChange={(value) => {
                            setIsSaleNameManual(true);
                            setSaleData((prev) => ({
                              ...prev,
                              saleName: value,
                            }));
                          }}
                          placeholder={
                            translate("UI.ENTER_SALE_NAME") || "Enter sale name"
                          }
                        />
                      </div>

                      <div className="col-md-4 w-45">
                        <Input
                          type="date"
                          label={translate("UI.SALE_DATE")}
                          value={saleData.saleDate}
                          onChange={handleDateChange}
                          required
                          prefix={<FiCalendar className="text-muted" />}
                          hint={translate("UI.SALE_DATE_DESC")}
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="time"
                          label={translate("UI.SALE_TIME")}
                          value={saleData.saleTime}
                          onChange={handleSaleTimeChange}
                          required
                          prefix={<FiClock className="text-muted" />}
                          hint={translate("UI.SALE_TIME_DESC")}
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="text"
                          inputMode="decimal"
                          label={
                            translate("UI.EXCHANGE_RATE") ||
                            "Döviz Kuru (USD/TRY)"
                          }
                          value={saleData.exchangeRate}
                          onChange={(value) =>
                            setSaleData((prev) => ({
                              ...prev,
                              exchangeRate: value,
                            }))
                          }
                          prefix={<FiDollarSign className="text-muted" />}
                          placeholder={fx?.rate ? String(fx.rate) : "42.156"}
                          hint="TCMB USD/TRY döviz kuru (Örn: 42.156 veya 42,156)"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Model Selection Section */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiServer className="text-info" />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">
                        {translate("UI.SELECT_MODELS") ||
                          translate("UI.SELECT_DEVICES")}
                      </h5>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Button
                        type="button"
                        appearance={
                          hasActiveDiscount ? "primary" : "primary-subtle"
                        }
                        icon={<FiPercent />}
                        onClick={openDiscountModal}
                        disabled={selectedModels.length === 0}
                      >
                        Iskonto Ayarlari
                      </Button>
                      <Button
                        appearance="primary"
                        onClick={() => setShowModelPicker(true)}
                        icon={<FiPackage />}
                      >
                        {translate("UI.ADD_MODEL") ||
                          translate("UI.ADD") ||
                          "Add Model"}
                      </Button>
                    </div>
                  </div>

                  <div className="section-content">
                    {selectedModels.length === 0 ? (
                      <div className="empty-models-container">
                        <FiServer size={64} className="text-muted opacity-50" />
                        <h6 className="text-muted mb-1 mt-3">
                          {translate("UI.NO_MODELS_SELECTED") ||
                            translate("UI.NO_DEVICES_SELECTED")}
                        </h6>
                        <p className="text-muted small mb-0">
                          {translate("UI.PLEASE_SELECT_MODELS") ||
                            translate("UI.PLEASE_SELECT_DEVICES") ||
                            "Click 'Add Model' to select models"}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="models-grid">
                          {selectedModels.map((item) => (
                            <div key={item.modelId} className="model-card">
                              <div className="model-card-header">
                                <div className="model-icon-wrapper">
                                  <FiPackage className="model-icon" />
                                </div>
                                <div className="model-info flex-grow-1">
                                  <div className="model-code">{item.code}</div>
                                  <div className="model-name">{item.name}</div>
                                </div>
                                <IconButton
                                  icon={<FiTrash2 />}
                                  appearance="danger-subtle"
                                  onClick={() =>
                                    handleRemoveModel(item.modelId)
                                  }
                                  title={translate("UI.REMOVE")}
                                />
                              </div>
                              <div className="model-card-divider"></div>
                              <div className="model-card-body">
                                <div className="model-detail-row">
                                  <span className="detail-label">
                                    {translate("UI.QUANTITY")}:
                                  </span>
                                  <div className="count-container">
                                    <Button
                                      appearance="subtle"
                                      size="sm"
                                      onClick={() =>
                                        handleUpdateModelCount(
                                          item.modelId,
                                          Math.max(1, item.count - 1),
                                        )
                                      }
                                    >
                                      -
                                    </Button>
                                    <Input
                                      type="number"
                                      value={item.count.toString()}
                                      onChange={(value) => {
                                        const num =
                                          parseInt(
                                            value.replace(/[^0-9]/g, ""),
                                          ) || 1;
                                        handleUpdateModelCount(
                                          item.modelId,
                                          num,
                                        );
                                      }}
                                      min={1}
                                      className="count-input"
                                    />
                                    <Button
                                      appearance="subtle"
                                      size="sm"
                                      onClick={() =>
                                        handleUpdateModelCount(
                                          item.modelId,
                                          item.count + 1,
                                        )
                                      }
                                    >
                                      +
                                    </Button>
                                  </div>
                                </div>
                                <div className="model-detail-row">
                                  <span className="detail-label">
                                    {translate("UI.PRICE_PER_UNIT")}:
                                  </span>
                                  <span className="model-price">
                                    <ProductPrice value={item.price || 0} />
                                  </span>
                                </div>
                                {hasActiveDiscount ? (
                                  (() => {
                                    const lineDisc = getLineDiscountInfo(
                                      item,
                                      discountOpts,
                                    );
                                    return (
                                      <>
                                        {saleData.discountScope ===
                                        "per_line" ? (
                                          <div className="model-detail-row">
                                            <span className="detail-label">
                                              Iskonto:
                                            </span>
                                            <span className="text-success fw-semibold">
                                              %
                                              {format(
                                                item.discountPercent ?? 0,
                                                "number",
                                              )}
                                            </span>
                                          </div>
                                        ) : (
                                          <>
                                            <div className="model-detail-row">
                                              <span className="detail-label">
                                                Liste tutari:
                                              </span>
                                              <span className="text-muted">
                                                <ProductPrice
                                                  value={lineDisc.gross}
                                                />
                                              </span>
                                            </div>
                                            <div className="model-detail-row">
                                              <span className="detail-label">
                                                Iskonto:
                                              </span>
                                              <span className="text-end">
                                                {saleData.orderHeaderKind ===
                                                "percent" ? (
                                                  <span className="text-success fw-semibold">
                                                    %
                                                    {format(
                                                      lineDisc.percent,
                                                      "number",
                                                    )}
                                                  </span>
                                                ) : (
                                                  <>
                                                    <span className="text-success fw-semibold">
                                                      -
                                                      <ProductPrice
                                                        value={lineDisc.amount}
                                                      />
                                                    </span>
                                                    <span className="text-muted small d-block">
                                                      ( %
                                                      {lineDisc.percent.toFixed(
                                                        1,
                                                      )}
                                                      )
                                                    </span>
                                                  </>
                                                )}
                                              </span>
                                            </div>
                                          </>
                                        )}
                                        <div className="model-total-row">
                                          <span className="detail-label fw-bold">
                                            Model net:
                                          </span>
                                          <span className="model-total">
                                            <ProductPrice
                                              value={lineNetTotal(item)}
                                            />
                                          </span>
                                        </div>
                                      </>
                                    );
                                  })()
                                ) : (
                                  <div className="model-total-row">
                                    <span className="detail-label fw-bold">
                                      {translate("UI.TOTAL")}:
                                    </span>
                                    <span className="model-total">
                                      <ProductPrice
                                        value={lineNetTotal(item)}
                                      />
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {hasActiveDiscount && (
                          <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
                            <span className="text-muted small">
                              {saleData.discountScope === "per_line"
                                ? "Model iskontolari sonrasi toplam:"
                                : "Liste ara toplami:"}
                            </span>
                            <span className="fw-semibold">
                              <ProductPrice
                                value={
                                  saleData.discountScope === "per_line"
                                    ? calculateLineNetSum()
                                    : calculateListSubtotal(selectedModels)
                                }
                              />
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {selectedModels.length > 0 && (
                      <div className="total-amount-container">
                        <div className="total-amount-header">
                          <span className="total-amount-label">
                            {translate("UI.TOTAL_AMOUNT")}:
                          </span>
                        </div>
                        <div className="total-amount-row">
                          <span className="total-amount-value">
                            <ProductPrice
                              value={calculateTotalAmount()}
                              rate={
                                parseExchangeRate(saleData.exchangeRate) ??
                                fx?.rate
                              }
                            />
                          </span>
                        </div>
                        {(parseExchangeRate(saleData.exchangeRate) ||
                          fx?.rate) && (
                          <div className="exchange-rate-info text-muted small mt-2">
                            <span>
                              {translate("UI.EXCHANGE_RATE") || "Döviz Kuru"}: 1
                              USD ={" "}
                              {Number(
                                parseExchangeRate(saleData.exchangeRate) ??
                                  fx?.rate,
                              ).toFixed(4)}{" "}
                              TL
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Odeme Bilgileri */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiCreditCard className="text-success" />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">
                        {translate("UI.PAYMENT_INFORMATION")}
                      </h5>
                      <p className="text-muted small mb-0">
                        {translate("UI.PAYMENT_METHOD_DESC")}
                      </p>
                    </div>
                  </div>

                  <div className="section-content">
                    <div className="row g-3 mb-4">
                      <div className="col-md-6 col-12">
                        <Label>{translate("UI.PAYMENT_METHOD")}</Label>
                        <div className="form-control-container">
                          <Dropdown
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <div className="d-flex align-items-center gap-2">
                                  <FiDollarSign className="text-muted" />
                                  <span>
                                    {translate(
                                      `UI.${saleData.paymentMethod.toUpperCase()}`,
                                    )}
                                  </span>
                                </div>
                              </div>
                            }
                          >
                            <DropdownContent>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentMethodChange("cash")
                                }
                                active={saleData.paymentMethod === "cash"}
                              >
                                <FiDollarSign className="me-2" />
                                {translate("UI.CASH")}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentMethodChange("credit_card")
                                }
                                active={
                                  saleData.paymentMethod === "credit_card"
                                }
                              >
                                <FiCreditCard className="me-2" />
                                {translate("UI.CREDIT_CARD")}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentMethodChange("bank_transfer")
                                }
                                active={
                                  saleData.paymentMethod === "bank_transfer"
                                }
                              >
                                <RiFileTransferLine className="me-2" />
                                {translate("UI.BANK_TRANSFER")}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentMethodChange(
                                    "documental_payment",
                                  )
                                }
                                active={
                                  saleData.paymentMethod ===
                                  "documental_payment"
                                }
                              >
                                <FiFileText className="me-2" />
                                {translate("UI.DOCUMENTAL_PAYMENT")}
                              </DropdownItem>
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>
                      <div className="col-md-6 col-12 mt-2">
                        <Label>{translate("UI.PAYMENT_STATUS")}</Label>
                        <div className="form-control-container">
                          <Dropdown
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <Lozenge
                                  appearance={getPaymentStatusAppearance(
                                    saleData.paymentStatus,
                                  )}
                                >
                                  {translate(
                                    `UI.${saleData.paymentStatus.toUpperCase()}`,
                                  )}
                                </Lozenge>
                              </div>
                            }
                          >
                            <DropdownContent>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange("pending")
                                }
                                active={saleData.paymentStatus === "pending"}
                              >
                                <Lozenge appearance="warning-subtle">
                                  {translate("UI.PENDING")}
                                </Lozenge>
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange("paid")
                                }
                                active={saleData.paymentStatus === "paid"}
                              >
                                <Lozenge appearance="success-subtle">
                                  {translate("UI.PAID")}
                                </Lozenge>
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  handlePaymentStatusChange("partial")
                                }
                                active={saleData.paymentStatus === "partial"}
                              >
                                <Lozenge appearance="info-subtle">
                                  {translate("UI.PARTIAL")}
                                </Lozenge>
                              </DropdownItem>
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12">
                        <Input
                          type="textarea"
                          label={translate("UI.NOTES")}
                          value={saleData.notes}
                          onChange={handleNotesChange}
                          hint={translate("UI.NOTES_DESC")}
                          rows={4}
                          placeholder={
                            translate("UI.ENTER_NOTES") ||
                            "Enter additional notes..."
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Butonlar */}
                <div className="form-actions">
                  <Button
                    appearance="danger-subtle"
                    onClick={() => navigate("/device-sales")}
                    title={translate("UI.CANCEL")}
                  >
                    {translate("UI.CANCEL")}
                  </Button>
                  <Button
                    appearance="primary-animated"
                    onClick={handleSubmit}
                    loading={loading}
                    icon={<FiShoppingCart />}
                    title={translate("UI.SAVE")}
                  >
                    {translate("UI.SAVE")}
                  </Button>
                </div>
              </>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
            autoDismiss={true}
          />
        ))}
      </ToastContainer>

      {/* Model Picker Modal */}
      <Modal
        isOpen={showModelPicker}
        onClose={() => setShowModelPicker(false)}
        scrollBehavior="inside"
        width={"medium"}
      >
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <div className="modal-header-icon">
              <FiPackage />
            </div>
            <div>
              <h5 className="mb-0">
                {translate("UI.SELECT_MODELS") ||
                  translate("UI.SELECT_DEVICES") ||
                  "Select Models"}
              </h5>
              <small className="text-muted">
                {translate("UI.SELECT_MODELS_DESC") ||
                  translate("UI.SELECT_DEVICES_DESC") ||
                  "Choose models to add to sale"}
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          {models.length === 0 ? (
            <div className="empty-models-container">
              <FiServer size={48} className="text-muted opacity-50" />
              <p className="text-muted mb-0 mt-2">
                {translate("UI.NO_MODELS_AVAILABLE") || "No models available"}
              </p>
            </div>
          ) : (
            models.map((model) => (
              <div
                key={model._id}
                className="model-picker-item"
                onClick={() => handleAddModel(model)}
              >
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                  <div className="model-picker-icon">
                    <FiPackage />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">
                      {model.code} - {model.name}
                    </div>
                    <div className="text-muted small">
                      <ProductPrice value={model.price || 0} />
                    </div>
                  </div>
                </div>
                <div className="model-picker-action">
                  <FiCheckCircle className="text-primary" />
                </div>
              </div>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="danger-subtle"
            onClick={() => setShowModelPicker(false)}
          >
            {translate("UI.CLOSE") || "Close"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Discount Settings Modal */}
      <Modal
        isOpen={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        scrollBehavior="inside"
        width="medium"
      >
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <div className="modal-header-icon">
              <FiPercent />
            </div>
            <div>
              <h5 className="mb-0">Iskonto Ayarlari</h5>
              <small className="text-muted">
                Siparis için iskonto türünü ve oranlarini belirleyin
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="mb-3 p-3 rounded border bg-white">
            <Label className="mb-2 d-block">Iskonto Türü</Label>
            <div className="d-flex flex-wrap gap-2">
              <Button
                type="button"
                appearance={
                  saleData.discountScope === "order_total"
                    ? "primary"
                    : "primary-subtle"
                }
                onClick={() => setDiscountScope("order_total")}
              >
                Genel toplam (liste fiyati üzerinden)
              </Button>
              <Button
                type="button"
                appearance={
                  saleData.discountScope === "per_line"
                    ? "primary"
                    : "primary-subtle"
                }
                onClick={() => setDiscountScope("per_line")}
              >
                Model bazli (%)
              </Button>
            </div>
          </div>

          {saleData.discountScope === "order_total" ? (
            <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
              <div className="flex-grow-1" style={{ minWidth: 180 }}>
                <Select
                  className="mb-0"
                  label="Genel iskonto türü"
                  unique
                  options={orderHeaderKindSelectOptions}
                  selected={[saleData.orderHeaderKind]}
                  onSelected={(values) => {
                    const v = values?.[0]?.value;
                    if (v === "percent") {
                      setSaleData((p) => ({
                        ...p,
                        orderHeaderKind: "percent",
                        orderDiscountFixed: 0,
                      }));
                    } else if (v === "fixed") {
                      setSaleData((p) => ({
                        ...p,
                        orderHeaderKind: "fixed",
                        orderDiscountPercent: 0,
                      }));
                    }
                  }}
                  placeholder="Tür seçin"
                />
              </div>
              <div className="flex-grow-1" style={{ minWidth: 160 }}>
                {saleData.orderHeaderKind === "percent" ? (
                  <Input
                    type="number"
                    label="Iskonto (%)"
                    value={String(saleData.orderDiscountPercent ?? 0)}
                    onChange={(v) =>
                      setSaleData((p) => ({
                        ...p,
                        orderDiscountPercent: Math.min(
                          100,
                          Math.max(
                            0,
                            parseFloat(
                              v.replace(/[^0-9.,]/g, "").replace(",", "."),
                            ) || 0,
                          ),
                        ),
                      }))
                    }
                    min={0}
                    max={100}
                    prefix={<FiPercent className="text-muted" />}
                  />
                ) : (
                  <Input
                    type="number"
                    label="Iskonto (USD)"
                    value={String(saleData.orderDiscountFixed ?? 0)}
                    onChange={(v) =>
                      setSaleData((p) => ({
                        ...p,
                        orderDiscountFixed: Math.max(
                          0,
                          parseFloat(
                            v.replace(/[^0-9.,]/g, "").replace(",", "."),
                          ) || 0,
                        ),
                      }))
                    }
                    min={0}
                    prefix={<FiDollarSign className="text-muted" />}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {selectedModels.length === 0 ? (
                <p className="text-muted mb-0">Önce model ekleyin.</p>
              ) : (
                <>
                  <div className="p-3 rounded border bg-light">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span className="small fw-semibold text-nowrap">
                        Toplu iskonto (%)
                      </span>
                      <div style={{ width: 100 }}>
                        <Input
                          type="number"
                          placeholder="0"
                          value={bulkDiscountPercent}
                          onChange={(v) =>
                            setBulkDiscountPercent(v.replace(/[^0-9.,]/g, ""))
                          }
                          className="discount-input mb-0"
                          min={0}
                          max={100}
                          prefix={<FiPercent className="text-muted" />}
                        />
                      </div>
                      <Button
                        type="button"
                        appearance="primary"
                        onClick={applyBulkLineDiscount}
                        disabled={bulkDiscountModelIds.length === 0}
                      >
                        Seçilenlere Uygula ({bulkDiscountModelIds.length})
                      </Button>
                    </div>
                    <Checkbox
                      value="select-all-discount-models"
                      label={
                        bulkDiscountModelIds.length === selectedModels.length
                          ? "Tüm seçimi kaldir"
                          : "Tüm modelleri seç"
                      }
                      isChecked={
                        selectedModels.length > 0 &&
                        bulkDiscountModelIds.length === selectedModels.length
                      }
                      isIndeterminate={
                        bulkDiscountModelIds.length > 0 &&
                        bulkDiscountModelIds.length < selectedModels.length
                      }
                      onChange={({ checked }) =>
                        toggleSelectAllBulkDiscountModels(!!checked)
                      }
                    />
                  </div>
                  <div
                    className="rounded border bg-white overflow-hidden"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <div
                      className="text-muted small fw-semibold border-bottom bg-light"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "32px 1fr 96px",
                        gap: "12px",
                        alignItems: "center",
                        padding: "8px 12px",
                      }}
                    >
                      <span />
                      <span>Model</span>
                      <span>Iskonto (%)</span>
                    </div>
                    {selectedModels.map((item, index) => (
                      <div
                        key={item.modelId}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "32px 1fr 96px",
                          gap: "12px",
                          alignItems: "start",
                          padding: "10px 12px",
                          borderBottom:
                            index < selectedModels.length - 1
                              ? "1px solid var(--m-border-color, #e9ecef)"
                              : "none",
                        }}
                      >
                        <div style={{ paddingTop: 2 }}>
                          <Checkbox
                            value={String(item.modelId)}
                            isChecked={bulkDiscountModelIds.includes(
                              item.modelId,
                            )}
                            onChange={({ checked }) =>
                              toggleBulkDiscountModel(item.modelId, !!checked)
                            }
                          />
                        </div>
                        <div className="min-w-0">
                          <div
                            className="fw-semibold"
                            style={{
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                            }}
                          >
                            {item.code}  {item.name}
                          </div>
                          <div className="text-muted small mt-1">
                            <ProductPrice value={item.price || 0} /> ×{" "}
                            {item.count}
                          </div>
                        </div>
                        <Input
                          type="number"
                          placeholder="0"
                          value={String(item.discountPercent ?? 0)}
                          onChange={(v) =>
                            handleLineDiscountChange(
                              item.modelId,
                              v.replace(/[^0-9.,]/g, ""),
                            )
                          }
                          className="discount-input mb-0"
                          min={0}
                          max={100}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
            {hasActiveDiscount && (
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted small">
                  {saleData.discountScope === "per_line"
                    ? "Model iskontolari sonrasi toplam"
                    : "Liste ara toplami"}
                </span>
                <span className="fw-semibold">
                  <ProductPrice
                    value={
                      saleData.discountScope === "per_line"
                        ? calculateLineNetSum()
                        : calculateListSubtotal(selectedModels)
                    }
                  />
                </span>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">Genel toplam</span>
              <span className="fw-semibold">
                <ProductPrice value={calculateTotalAmount()} />
              </span>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          {hasActiveDiscount && (
            <Button appearance="danger-subtle" onClick={clearAllDiscounts}>
              Iskontolari Temizle
            </Button>
          )}
          <Button
            appearance="primary"
            onClick={() => setShowDiscountModal(false)}
          >
            Tamam
          </Button>
        </ModalFooter>
      </Modal>

      <style>
        {`
                .modern-card {
                    border-radius: 12px;
                    border: 1px solid #e9ecef;
                    overflow: hidden;
                }

                .section-container {
                    margin-bottom: 2rem;
                    padding: 1.75rem;
                    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
                    border-radius: 12px;
                    border: 1px solid #e9ecef;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                    transition: all 0.3s ease;
                }

                .section-container:hover {
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
                }

                .section-container:last-child {
                    margin-bottom: 0;
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 1.75rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #f1f3f5;
                }

                .section-icon {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
                    border-radius: 12px;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
                    flex-shrink: 0;
                }

                .section-icon svg {
                    width: 24px;
                    height: 24px;
                    color: white;
                }

                .section-title {
                    flex: 1;
                }

                .section-title h5 {
                    font-size: 1.15rem;
                    font-weight: 600;
                    margin: 0 0 0.25rem 0;
                    color: #1a202c;
                }

                .section-title p {
                    margin: 0;
                    line-height: 1.4;
                }

                .section-content {
                    padding: 0.5rem 0 0 0;
                }

                .form-control-container {
                    background: #fff;
                    border: 1px solid #e2e5e8;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }

                .form-control-container:hover {
                    border-color: #00b4f3;
                    box-shadow: 0 0 0 3px rgba(12, 132, 121, 0.1);
                }

                .dropdown-text {
                    flex: 1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .device-selection-grid {
                    display: flex;
                    gap: 1.5rem;
                    flex-wrap: wrap;
                }

                .device-selector-card {
                    flex: 1 1 360px;
                    border: 1px solid #edf1f5;
                    border-radius: 12px;
                    padding: 1.25rem;
                    background: #fff;
                    box-shadow: 0 10px 25px rgba(15, 23, 42, 0.04);
                }

                .device-selector-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.75rem;
                }

                .device-step-chip {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--bs-primary-bg-subtle);
                    color: var(--bs-primary);
                    font-weight: 700;
                    font-size: 0.9rem;
                }

                .device-selector-controls {
                    display: flex;
                    gap: 0.75rem;
                    align-items: flex-end;
                }

                .random-select-btn {
                    height: fit-content;
                    margin-top: 1.35rem;
                }

                .device-selector-disabled {
                    opacity: 0.6;
                    pointer-events: none;
                }

                .model-meta-card {
                    border: 1px dashed #cbd5f5;
                    border-radius: 12px;
                    padding: 0.75rem 1rem;
                    background: #f8faff;
                    display: flex;
                    flex-direction: column;
                    gap: 0.35rem;
                }

                .model-meta-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .device-option {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                }

                .device-price-chip {
                    padding: 0.15rem 0.65rem;
                    border-radius: 999px;
                    background: #ecfdf3;
                    color: #15803d;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .device-progress {
                    border: 1px solid #edf1f5;
                    border-radius: 12px;
                    padding: 0.75rem 1rem;
                    background: #f8fafc;
                }

                .device-progress-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 0.5rem;
                }

                .device-progress-bar {
                    width: 100%;
                    height: 8px;
                    border-radius: 999px;
                    background: #e4e7ec;
                    overflow: hidden;
                }

                .device-progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #2563eb, #0ea5e9);
                    transition: width 0.3s ease;
                }

                .empty-models-container {
                    min-height: 240px;
                    border: 2px dashed #dee2e6;
                    border-radius: 16px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: #94a3b8;
                    gap: 0.5rem;
                    padding: 3rem 2rem;
                    background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
                }

                .empty-models-container h6 {
                    color: #495057;
                    font-weight: 600;
                }

                .models-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 1.25rem;
                }

                @media (max-width: 768px) {
                    .models-grid {
                        grid-template-columns: 1fr;
                    }
                }

                .model-card {
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 0;
                    background: #fff;
                    transition: all 0.3s ease;
                    overflow: hidden;
                }

                .model-card:hover {
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    transform: translateY(-4px);
                }

                .model-card-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1rem;
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                }

                .model-icon-wrapper {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
                    border-radius: 10px;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
                    flex-shrink: 0;
                }

                .model-icon {
                    width: 20px;
                    height: 20px;
                    color: white;
                }

                .model-info {
                    flex: 1;
                    min-width: 0;
                }

                .model-code {
                    font-size: 14px;
                    font-weight: 700;
                    margin-bottom: 4px;
                    color: #1a202c;
                }

                .model-name {
                    font-size: 13px;
                    color: #6c757d;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .model-card-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #e9ecef, transparent);
                }

                .model-card-body {
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .model-detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 0.5rem;
                }

                .detail-label {
                    font-size: 13px;
                    color: #6c757d;
                    font-weight: 500;
                }

                .count-container {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .count-input {
                    width: 50px !important;
                    text-align: center;
                    font-weight: 600;
                    padding: 0 !important;
                }

                .count-input input {
                    text-align: center;
                    padding: 0.25rem 0.5rem;
                    font-weight: 600;
                }

                .discount-input {
                    max-width: 100px !important;
                }

                .discount-input input {
                    text-align: right;
                    padding: 0.35rem 0.5rem;
                }

                .model-price {
                    font-size: 14px;
                    font-weight: 600;
                    color: #00b4f3;
                }

                .model-total-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-top: 0.75rem;
                    border-top: 1px solid #e9ecef;
                }

                .model-total {
                    font-size: 16px;
                    font-weight: 700;
                    color: #00b4f3;
                }

                .total-amount-container {
                    margin-top: 1.5rem;
                    padding: 1.25rem;
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                    border: 2px solid #00b4f3;
                    border-radius: 12px;
                }

                .total-amount-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.75rem;
                }

                .total-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .total-amount-label {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1a202c;
                }

                .total-amount-value {
                    font-size: 24px;
                    font-weight: 700;
                    color: #00b4f3;
                }

                .total-amount-value-usd {
                    font-size: 20px;
                    font-weight: 600;
                    color: #059669;
                }

                .exchange-rate-info {
                    padding-top: 0.5rem;
                    border-top: 1px solid #e9ecef;
                    text-align: center;
                }

                @media (max-width: 576px) {
                    .total-amount-container {
                        padding: 1rem;
                    }

                    .total-amount-label {
                        font-size: 16px;
                    }

                    .total-amount-value {
                        font-size: 20px;
                    }
                }

                .model-picker-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.25rem;
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    margin-bottom: 0.75rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: #fff;
                }

                .model-picker-item:hover {
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                    border-color: #00b4f3;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.15);
                    transform: translateX(4px);
                }

                .model-picker-item .fw-semibold {
                    color: #1a202c;
                    font-weight: 600;
                }

                .model-picker-item .text-muted {
                    color: #00b4f3;
                }

                .model-picker-item svg {
                    transition: transform 0.3s ease;
                }

                .model-picker-item:hover svg {
                    transform: scale(1.1);
                }

                .modal-header-icon {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
                    border-radius: 10px;
                    box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
                }

                .modal-header-icon svg {
                    width: 20px;
                    height: 20px;
                    color: white;
                }

                .model-picker-icon {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
                    border-radius: 8px;
                    flex-shrink: 0;
                }

                .model-picker-icon svg {
                    width: 18px;
                    height: 18px;
                    color: #6c757d;
                }

                .model-picker-action {
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .model-picker-item:hover .model-picker-action {
                    opacity: 1;
                }

                .model-picker-action svg {
                    width: 24px;
                    height: 24px;
                }

                .selected-devices-container {
                    // background: #f8f9fa;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                }

                .pricing-details {
                    background: #f8f9fa;
                    padding: 0.75rem;
                    border-radius: 6px;
                }

                .pricing-details .border-top {
                    border-color: #dee2e6 !important;
                }

                .total-amount {
                    margin-top: 1rem;
                    text-align: right;
                    padding: 1rem;
                    background: #fff;
                    border-radius: 6px;
                    border: 1px solid #e9ecef;
                }

                .total-amount h5 {
                    margin: 0;
                    color: var(--bs-primary);
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    gap: 1rem;
                    margin-top: 0;
                    padding: 1.5rem;
                    border-top: 2px solid #f1f3f5;
                    background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
                    border-radius: 0 0 12px 12px;
                }

                @media (max-width: 576px) {
                    .form-actions {
                        flex-direction: column-reverse;
                        gap: 0.75rem;
                    }

                    .form-actions button {
                        width: 100%;
                    }
                }

                /* Additional responsive improvements */
                @media (max-width: 768px) {
                    .section-container {
                        padding: 1.25rem;
                    }

                    .section-header {
                        flex-direction: row;
                        align-items: center;
                    }

                    .section-icon {
                        width: 42px;
                        height: 42px;
                    }

                    .section-title h5 {
                        font-size: 1rem;
                    }

                    .row.g-3 {
                        gap: 1rem !important;
                    }
                }

                /* Smooth animations */
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .section-container {
                    animation: slideIn 0.4s ease-out;
                }

                .model-card {
                    animation: slideIn 0.3s ease-out;
                }

                /* Improved input focus states */
                input:focus,
                textarea:focus,
                select:focus {
                    outline: none;
                    border-color: #00b4f3 !important;
                    box-shadow: 0 0 0 3px rgba(12, 132, 121, 0.1) !important;
                }

                /* Pricing UI improvements */
                .selected-devices-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 12px;
                }

                .device-card {
                    border: 1px solid #edf1f5;
                    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.04);
                    transition: transform .15s ease, box-shadow .15s ease;
                }

                .device-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16, 24, 40, 0.08);
                }

                .device-index {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 22px;
                    height: 22px;
                    border-radius: 6px;
                    background: var(--bs-primary-bg-subtle);
                    color: var(--bs-primary);
                    font-size: 12px;
                    font-weight: 700;
                }

                .price-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 10px;
                    background: linear-gradient(180deg, #eef4ff 0%, #e6efff 100%);
                    color: #007d70;
                    border: 1px solid #d6e4ff;
                    border-radius: 999px;
                    font-weight: 700;
                    font-size: 0.95rem;
                }

                .discount-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 999px;
                    background: #e8f5e9;
                    color: #2e7d32;
                    border: 1px solid #c8e6c9;
                    font-weight: 600;
                    font-size: 12px;
                }

                .discount-progress {
                    position: relative;
                    width: 100%;
                    height: 6px;
                    border-radius: 999px;
                    background: #eef2f6;
                    overflow: hidden;
                    margin-bottom: 4px;
                }

                .discount-progress-bar {
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    background: linear-gradient(90deg, #16a34a, #86efac);
                }
                .pagination-custom {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                }
                /* Pager custom styles */
                 .page-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    border-radius: 8px;
                    border: 1px solid #e6e9ee;
                    color: var(--bs-gray-700);
                    background: #fff;
                    box-shadow: 0 1px 0 rgba(16, 24, 40, 0.04);
                    transition: all .15s ease;
                }
                .pagination-custom .page-item {
                    list-style: none;
                }
                .pagination-custom .page-item + .page-item .page-link {
                    margin-left: 6px;
                }

                .pagination-custom .page-link:hover {
                    border-color: var(--bg-primary);
                    color: var(--bg-primary);
                }

                .pagination-custom .page-item.active .page-link {
                    color: var(--bg-primary);
                    background: linear-gradient(180deg, var(--bg-primary-subtle) 0%, var(--bg-primary-subtle) 100%);
                    border-color: var(--bg-primary-subtle);
                    font-weight: 700;
                }

                .pagination-custom .page-item.disabled .page-link {
                    color: #98a2b3;
                    background: #f8fafc;
                }

                .pagination-custom .page-link {
                    min-width: 36px;
                    height: 36px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    border: 1px solid #e6e9ee; /* differentiate from buttons */
                    background: #fff;
                    color: #98a2b3;
                    box-shadow: 0 1px 0 rgba(16, 24, 40, 0.04);
                    padding: 0;
                    line-height: 1;
                    cursor: default;
                }

                /* keep dots subtle on hover/focus */
                .pagination-custom .page-item.disabled .page-link,
                .pagination-custom .page-link:hover,
                .pagination-custom .page-link:focus {
                    border-color: #e6e9ee;
                    color: #98a2b3;
                    background: #fff;
                    box-shadow: 0 1px 0 rgba(16, 24, 40, 0.04);
                    cursor: pointer;
                }

                /* Partial Delivery Section Styles */
                .partial-delivery-section {
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    padding: 1.5rem;
                    background: #fff;
                    margin-top: 1.5rem;
                }

                .partial-delivery-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                    flex-wrap: wrap;
                    margin-bottom: 1.5rem;
                }

                .partial-delivery-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: var(--bs-primary-bg-subtle);
                    color: var(--bs-primary);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }

                .partial-delivery-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 0.35rem 0.75rem;
                    border-radius: 999px;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    font-weight: 600;
                    color: #475467;
                }

                .delivery-stat-card {
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    width: 30%;
                    gap: 1rem;
                    background: #f8f9fa;
                }
                .delivery-plan-card-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
        .delivery-plan-card {
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 1rem;
                    background: #fff;
                    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.05);
                }
                .plan-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }
                .plan-header-toggle {
                    border: none;
                    background: transparent;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    padding: 0;
                    color: inherit;
                }
                .plan-header-toggle:focus {
                    outline: none;
                }
                .plan-header-info small {
                    display: block;
                }
                .plan-chevron {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    border: 1px solid #d5dae0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.25s ease, background 0.25s ease;
                    color: #475467;
                }
                .plan-chevron.expanded {
                    transform: rotate(180deg);
                    background: var(--bs-primary-bg-subtle);
                    color: var(--bs-primary);
                    border-color: var(--bs-primary);
                }
                .delivery-plan-card .plan-meta {
                    margin-top: 0.5rem;
                    display: flex;
                    justify-content: flex-end;
                }
                .plan-device-list {
                    margin-top: 0.75rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    max-height: 600px;
                    overflow: hidden;
                    transition: max-height 0.25s ease, margin-top 0.25s ease;
                }
                .plan-device-list.collapsed {
                    max-height: 0;
                    margin-top: 0;
                }
                .plan-device-group {
                    border: 1px solid #edf1f5;
                    border-radius: 10px;
                    padding: 0.75rem;
                    background: #fff;
                }
                .plan-device-group + .plan-device-group {
                    margin-top: 0.75rem;
                }
                .plan-device-group-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 0.5rem;
                }
                .plan-group-toggle {
                    border: none;
                    background: transparent;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    padding: 0;
                    color: inherit;
                }
                .plan-group-toggle:focus {
                    outline: none;
                }
                .plan-group-chevron {
                    width: 26px;
                    height: 26px;
                    border-radius: 50%;
                    border: 1px solid #d5dae0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.25s ease, background 0.25s ease;
                    color: #475467;
                }
                .plan-group-chevron.expanded {
                    transform: rotate(180deg);
                    background: var(--bs-primary-bg-subtle);
                    color: var(--bs-primary);
                    border-color: var(--bs-primary);
                }
                .plan-device-group-title {
                    display: flex;
                    flex-direction: column;
                    text-align: left;
                }
                .plan-device-group-title small {
                    color: #98a2b3;
                    font-weight: 500;
                }
                .plan-device-chips {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    max-height: 600px;
                    overflow: hidden;
                    transition: max-height 0.25s ease, margin-top 0.25s ease;
                }
                .plan-device-chips.collapsed {
                    max-height: 0;
                    margin-top: 0;
                }
                .plan-device-chip {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.5rem 0.75rem;
                    border: 1px solid #e4e7ec;
                    border-radius: 8px;
                    background: #f8fafc;
                }
                .plan-device-info {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .plan-device-hardware {
                    font-size: 12px;
                    color: #475467;
                }
                .device-assignment-select {
                    min-width: 220px;
                }
                .delivery-plan-select {
                    width: 100%;
                    border-radius: 8px;
                    border: 1px solid #d0d5dd;
                    padding: 0.45rem 0.75rem;
                    background: #fff;
                }
                    @media (max-width: 600px) {
                        .delivery-stat-card-container {
                            flex-direction: column;
                            align-items: center;
                            gap: 0.5rem;
                        }
                            .delivery-stat-card {
                                width: 100%;
                            }
                    }

                .delivery-stat-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    flex-shrink: 0;
                }

                .delivery-stat-icon.delivered {
                    background: rgba(16, 185, 129, 0.15);
                    color: #059669;
                }

                .delivery-stat-icon.total {
                    background: rgba(59, 130, 246, 0.15);
                    color: #2563eb;
                }

                .delivery-stat-icon.pending {
                    background: rgba(245, 158, 11, 0.18);
                    color: #b45309;
                }

                .delivery-progress-card {
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 1.25rem;
                    background: #f8f9fa;
                    height: 100%;
                }

                .delivery-progress {
                    width: 100%;
                    height: 10px;
                    border-radius: 999px;
                    background: #e9ecef;
                    overflow: hidden;
                }

                .delivery-progress-bar {
                    height: 100%;
                    background: linear-gradient(90deg, #007d70 0%, #005249 100%);
                    border-radius: 999px;
                    transition: width 0.3s ease;
                }

                .delivered-devices-container {
                    margin-top: 1.5rem;
                }

                .devices-list-wrapper {
                    background: #fff;
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    padding: 0;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .model-group-container {
                    border-bottom: 1px solid #e9ecef;
                }

                .model-group-container:last-child {
                    border-bottom: none;
                }

                .model-group-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.9rem 1.25rem;
                    background: #fafbfc;
                    cursor: pointer;
                    transition: background 0.2s ease;
                    user-select: none;
                }

                .model-group-header:hover {
                    background: #f3f5f7;
                }

                .model-group-info {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex: 1;
                }

                .model-chevron {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    border: 1px solid #d5dae0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    color: #475467;
                    transition: transform 0.25s ease, background 0.2s ease;
                }

                .model-chevron.expanded {
                    transform: rotate(180deg);
                    background: #007d70;
                    color: #fff;
                    border-color: var(--bs-primary);
                }

                .model-badge {
                    font-size: 13px;
                    font-weight: 600;
                    padding: 0.35rem 0.75rem;
                    border-radius: 999px;
                }

                .model-group-title {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .model-name {
                    font-weight: 600;
                    font-size: 15px;
                    color: #1d2939;
                }

                .model-count {
                    font-size: 12px;
                    color: #667085;
                }

                .model-group-stats {
                    display: flex;
                    align-items: center;
                }

                .model-progress-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.35rem;
                    padding: 0.35rem 0.7rem;
                    border-radius: 999px;
                    background: #eef4ff;
                    color: #007d70;
                    font-weight: 600;
                    font-size: 13px;
                }

                .progress-icon {
                    color: #22c55e;
                }

                .model-devices-list {
                    padding: 0 1.25rem;
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.3s ease, padding 0.3s ease;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .model-devices-list.expanded {
                    max-height: 2000px;
                    padding: 0.75rem 1.25rem 1rem;
                }

                .model-devices-list.collapsed {
                    max-height: 0;
                    padding: 0 1.25rem;
                }

                .devices-list-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .devices-list-wrapper::-webkit-scrollbar-track {
                    background: #f4f6f8;
                    border-radius: 4px;
                }

                .devices-list-wrapper::-webkit-scrollbar-thumb {
                    background: #cfd4dc;
                    border-radius: 4px;
                }

                .device-delivery-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0.85rem 1rem;
                    border: 1px solid #e3e8ef;
                    border-radius: 10px;
                    background: #fff;
                    transition: all 0.2s ease;
                }

                .device-delivery-item:hover {
                    border-color: var(--bs-primary);
                    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
                }

                .device-delivery-item.delivered {
                    border-color: #86efac;
                    background: #f0fdf4;
                }

                .device-info {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    flex: 1;
                }

                .device-number {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    background: #eef2ff;
                    color: #007d70;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    flex-shrink: 0;
                }

                .device-delivery-item.delivered .device-number {
                    background: #dcfce7;
                    color: #15803d;
                }

                .device-details {
                    flex: 1;
                }

                .device-serial {
                    font-weight: 600;
                    color: #1d2939;
                    font-size: 15px;
                    margin-bottom: 4px;
                }

                .device-hardware {
                    font-size: 13px;
                    color: #667085;
                }

                .device-checkbox-wrapper {
                    flex-shrink: 0;
                }

                .delivery-checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 0.65rem;
                    cursor: pointer;
                    user-select: none;
                }

                .delivery-checkbox {
                    display: none;
                }

                .checkbox-custom {
                    width: 24px;
                    height: 24px;
                    border-radius: 6px;
                    border: 1px solid #cfd4dc;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    background: #fff;
                }

                .delivery-checkbox:checked + .checkbox-custom {
                    background: #0f9d58;
                    border-color: #0f9d58;
                }

                .check-icon {
                    color: #fff;
                    font-size: 16px;
                }

                .checkbox-label-text {
                    font-weight: 500;
                    color: #475467;
                    font-size: 14px;
                }

                .delivery-checkbox:checked ~ .checkbox-label-text {
                    color: #0f9d58;
                }

                .empty-devices-state {
                    text-align: center;
                    padding: 3rem 1rem;
                    color: #94a3b8;
                }

                .empty-icon {
                    font-size: 48px;
                    margin-bottom: 1rem;
                    opacity: 0.4;
                }

                @media (max-width: 768px) {
                    .section-container {
                        padding: 1rem;
                    }

                    .section-header {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        gap: 0.5rem;
                    }

                    .section-icon {
                        width: 32px;
                        height: 32px;
                    }

                    .section-icon svg {
                        width: 20px;
                        height: 20px;
                    }

                    .selected-devices-grid {
                        grid-template-columns: 1fr;
                    }

                    .partial-delivery-section {
                        padding: 1.25rem;
                    }

                        .delivery-stats-card {
                            flex-direction: column;
                            gap: 0.75rem;
                        }

                        .delivery-stat-divider {
                            width: 100%;
                            height: 1px;
                        }

                        .device-delivery-item {
                            flex-direction: column;
                            align-items: flex-start;
                            gap: 1rem;
                        }

                        .device-checkbox-wrapper {
                            width: 100%;
                        }

                        .delivery-checkbox-label {
                            width: 100%;
                            justify-content: space-between;
                        }
                    }
                }
                `}
      </style>
    </AppPage>
  );
};

// CSS ekleyelim
const styles = `
.single-select .select__option {
    cursor: pointer;
    padding: 8px 12px;
}

.single-select .select__option:hover {
    background-color: var(--bs-primary-subtle);
}

.single-select .select__option--is-selected {
    background-color: var(--bs-primary-subtle);
    color: var(--bs-primary);
}

.single-select .select__option--is-focused {
    background-color: var(--bs-primary-subtle);
}

.single-select .select__multi-value {
    background-color: var(--bs-primary-subtle);
    color: var(--bs-primary);
}
`;

// Style tag'ini head'e ekle
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default withAuth(CreateDeviceSale, ["admin", "employee", "user"]);
